import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setUser } from '../store/reducers/user';
import axios from 'axios';
import { BASE_URL } from '../utils/BaseUrl';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
 
// Importing the images
import googleIcon from "../assets/images/google_icon.png";
import mSecureLogo from "../assets/images/logo.png";
import mSecureIcon from "../assets/images/logo-sm-dark.png";
 
const FEATURES = [
  { icon: ShieldOutlinedIcon, title: 'Zero-Trust Security', desc: 'Every session verified with device posture checks and strong authentication.' },
  { icon: PolicyOutlinedIcon, title: 'Policy Enforcement', desc: 'Clipboard, downloads, uploads, printing and screen controls — all manageable.' },
  { icon: FingerprintOutlinedIcon, title: 'Windows Hello SSO', desc: 'Biometric and PIN-based sign-in for a seamless, password-free experience.' },
  { icon: BlockOutlinedIcon, title: 'Ad & Tracker Blocking', desc: 'Built-in adblock keeps browsing fast, private and distraction-free.' },
  { icon: FilterAltOutlinedIcon, title: 'URL Filtering', desc: 'Restrict browsing to approved domains and keep your workforce on track.' },
  { icon: BrandingWatermarkOutlinedIcon, title: 'Dynamic Watermarking', desc: 'Screen watermarks deter leaks and keep sensitive content traceable.' }
];
 
export default function LoginBoxLikeFlutter() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const methods = useForm();
 
  const BTN_DARK = '#243454';
  const ACCENT_BLUE = '#60a5fa';
 
  useEffect(() => {
    setMounted(true);
  }, []);
 
  const onFormSubmitHandler = async (formValue) => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await axios.post(`${BASE_URL}auth/login`, formValue);
      const ok = response?.data?.success || !!response?.data?.session_Id;
      if (ok) {
        const user = response.data.user || response.data.user;
        const appToken = response.data.token || response.data.session_Id || "";
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', appToken);
        localStorage.setItem('loginTime', new Date().toISOString());
        dispatch(setUser(user));
        window.location.href = "/home";
      } else {
        const msg = response.data.message || 'Something went wrong';
        setErrorMessage(msg);
      }
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.response?.data?.message || 'Something went wrong';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };
 
  const handleGoogle = () => window.location.href = `${BASE_URL}auth/oauth/google`;
 
  // Scaled down text fields to mimic 75% zoom
  const fieldSx = {
    mb: 1.5,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#f8fafc',
      fontSize: '0.85rem',
      height: '40px',
      '& fieldset': { borderColor: '#d1d5db' },
      '&:hover fieldset': { borderColor: '#9ca3af' },
      '&.Mui-focused fieldset': { borderColor: BTN_DARK, borderWidth: '1px' }
    }
  };
 
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
 
      {/* ---------- left: form panel ---------- */}
      <Box
        sx={{
          flex: { md: '0 0 35%' },
          bgcolor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 4 }
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 280 }}>
         
          {/* Logo added with adjusted spacing and exact alignment */}
          <Fade in={mounted} timeout={600} style={{ transitionDelay: mounted ? '20ms' : '0ms' }}>
            <Box sx={{
              mb: 1.5, // Reduced space between logo and "Welcome back"
              display: 'flex',
              justifyContent: 'flex-start'
            }}>
              <Box
                component="img"
                src={mSecureLogo}
                alt="mSecure Logo"
                sx={{
                  height: '45px',
                  width: 'auto',
                  objectFit: 'contain',
                  transform: 'translateX(-4px)' // Fixes the indentation gap caused by image's transparent whitespace
                }}
              />
            </Box>
          </Fade>
 
          <Fade in={mounted} timeout={600} style={{ transitionDelay: mounted ? '80ms' : '0ms' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5, fontSize: '1.3rem' }}>
              Welcome back
            </Typography>
          </Fade>
 
          <Fade in={mounted} timeout={600} style={{ transitionDelay: mounted ? '140ms' : '0ms' }}>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 3, fontSize: '0.75rem' }}>
              Sign in to continue to mSecure
            </Typography>
          </Fade>
 
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onFormSubmitHandler)}>
              <Fade in={mounted} timeout={600} style={{ transitionDelay: mounted ? '200ms' : '0ms' }}>
                <TextField
                  placeholder="Email"
                  fullWidth
                  {...methods.register('email')}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon sx={{ color: '#94a3b8', fontSize: '1.1rem' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Fade>
 
              <Fade in={mounted} timeout={600} style={{ transitionDelay: mounted ? '260ms' : '0ms' }}>
                <TextField
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  {...methods.register('password')}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: '#94a3b8', fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff sx={{ fontSize: '1.1rem' }} /> : <Visibility sx={{ fontSize: '1.1rem' }} />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Fade>
 
              <Fade in={mounted} timeout={600} style={{ transitionDelay: mounted ? '320ms' : '0ms' }}>
                <FormControlLabel
                  control={<Checkbox size="small" {...methods.register('remember')} sx={{ color: '#cbd5e1', padding: '4px 8px' }} />}
                  label={<Typography variant="body2" sx={{ color: '#475569', fontSize: '0.75rem' }}>Remember me</Typography>}
                  sx={{ mb: 2, ml: -0.5 }}
                />
              </Fade>
 
              {errorMessage && (
                <Fade in={!!errorMessage}>
                  <Typography color="error" variant="body2" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                    {errorMessage}
                  </Typography>
                </Fade>
              )}
 
              <Fade in={mounted} timeout={600} style={{ transitionDelay: mounted ? '380ms' : '0ms' }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                  sx={{
                    py: 1,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: '#ffffff',
                    backgroundColor: BTN_DARK,
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#1e293b', boxShadow: 'none' },
                  }}
                >
                  {loading ? 'Signing in…' : 'Login'}
                </Button>
              </Fade>
 
              <Fade in={mounted} timeout={600} style={{ transitionDelay: mounted ? '440ms' : '0ms' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>OR</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>
              </Fade>
 
              <Fade in={mounted} timeout={600} style={{ transitionDelay: mounted ? '500ms' : '0ms' }}>
                <Button
                  onClick={handleGoogle}
                  fullWidth
                  variant="outlined"
                  startIcon={<Box component="img" src={googleIcon} alt="" sx={{ width: 14, height: 14 }} />}
                  sx={{
                    py: 0.8,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    color: '#334155',
                    borderColor: '#e2e8f0',
                    backgroundColor: '#ffffff',
                    '&:hover': { backgroundColor: '#f8fafc', borderColor: '#cbd5e1' }
                  }}
                >
                  Continue with Google
                </Button>
              </Fade>
            </form>
          </FormProvider>
        </Box>
      </Box>
 
      {/* ---------- right: feature panel ---------- */}
      <Box
        sx={{
          flex: { md: '0 0 65%' },
          background: 'linear-gradient(to right, rgb(45, 156, 219) 0%, rgb(11, 29, 81) 100%)',
          p: { xs: 3, sm: 6 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ maxWidth: '750px', mx: 'auto', w: '100%' }}>
         
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box
              component="img"
              src={mSecureIcon}
              alt="mSecure Icon"
              sx={{ width: 28, height: 28, objectFit: 'contain' }}
            />
            <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1.25rem' }}>
              mSecure Browser
            </Typography>
          </Box>
         
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', mb: 3, fontSize: '0.75rem', pl: 0.5 }}>
            Enterprise-grade secure browsing — built for modern workforces.
          </Typography>
 
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
              mb: 3
            }}
          >
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <Box
                key={title}
                sx={{
                  p: 1.5,
                  borderRadius: '10px',
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 10px 24px rgba(0, 0, 0, 0.25)',
                  }
                }}
              >
                <Icon sx={{ fontSize: 18, color: '#ffffff', mb: 0.5 }} />
                <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.2, fontSize: '0.8rem' }}>
                  {title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.3, fontSize: '0.7rem' }}>
                  {desc}
                </Typography>
              </Box>
            ))}
          </Box>
 
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, pl: 0.5 }}>
            <VerifiedIcon sx={{ fontSize: 14, color: '#ffffff' }} />
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontWeight: 500, fontSize: '0.7rem' }}>
              Trusted by enterprises worldwide
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}